import React, { useState } from 'react';

const HT = {
  yellow: '#FFD166', yellowSoft: '#FFE9A8', yellowBg: '#FFF8E7',
  green: '#88C057', greenDark: '#5E9E3E', greenSoft: '#D4E8C0', greenBg: '#E8F3D9',
  coral: '#FF8C42', coralDark: '#E76F51', coralPress: '#B8431E',
  pinkBg: '#FCE4E8', pink: '#F4A6B8',
  brown: '#8B6F47', brownDark: '#6B4F2B',
  text: '#2D2D2D', textSoft: '#6B6B6B', textMuted: '#9A9A9A',
  border: '#E8E0CC', white: '#FFFFFF',
};
const FONT_DISPLAY = '"Fredoka", "Baloo 2", "Nunito", sans-serif';
const FONT_BODY = '"Nunito", "Baloo 2", sans-serif';

const OPT_LABELS = ['A', 'B', 'C', 'D', 'E', 'F'];

export default function TopicMCQGame({ payload, onAnswer, disabled }) {
  const [picked, setPicked] = useState(null);
  const correctIdx = payload.correct_index;
  const opts = payload.options || [];

  const click = (idx) => {
    if (picked !== null || disabled) return;
    setPicked(idx);
    if (onAnswer) onAnswer({ is_correct: idx === correctIdx, user_answer: idx });
  };

  const cueKind = payload.cue_kind;
  const isLocked = picked !== null || disabled;

  // ─── Cue ──────────────────────────────────────────────────
  const Cue = () => (
    <div
      style={{
        padding: 'clamp(20px, 3vw, 32px)',
        background: `linear-gradient(180deg, ${HT.yellowBg} 0%, ${HT.greenBg} 100%)`,
        border: `2px dashed ${HT.yellow}`,
        borderRadius: 22,
        textAlign: 'center',
        marginBottom: 20,
        minHeight: 200,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: FONT_BODY,
      }}
    >
      {cueKind === 'image' && payload.image_url ? (
        <img
          src={payload.image_url}
          alt=""
          style={{
            maxHeight: 240, maxWidth: '100%',
            borderRadius: 16, objectFit: 'contain',
            filter: 'drop-shadow(0 4px 8px rgba(139,111,71,0.15))',
          }}
          onError={(e) => { e.target.style.display = 'none'; }}
        />
      ) : cueKind === 'word_en' ? (
        <div>
          <div style={{ fontSize: 'clamp(36px, 5vw, 56px)', fontWeight: 700, fontFamily: FONT_DISPLAY, color: HT.text, lineHeight: 1.1 }}>
            {payload.cue_text}
          </div>
          {payload.pronunciation_uk ? (
            <div style={{ marginTop: 6, fontSize: 16, color: HT.textSoft, fontFamily: 'monospace' }}>
              {payload.pronunciation_uk}
            </div>
          ) : null}
          {payload.pos ? (
            <div style={{ marginTop: 4, fontStyle: 'italic', color: HT.textMuted, fontSize: 14 }}>
              ({payload.pos})
            </div>
          ) : null}
        </div>
      ) : (
        <div style={{ fontSize: 'clamp(18px, 2.4vw, 22px)', color: HT.text, fontWeight: 600, lineHeight: 1.4 }}>
          {payload.cue_text}
        </div>
      )}
    </div>
  );

  return (
    <div style={{ maxWidth: 760, margin: '0 auto', fontFamily: FONT_BODY }}>
      {/* Question prompt */}
      {payload.question_vi || payload.question_en ? (
        <div style={{
          marginBottom: 12, textAlign: 'center',
          fontSize: 'clamp(15px, 1.8vw, 17px)', fontWeight: 700,
          fontFamily: FONT_DISPLAY, color: HT.brownDark,
        }}>
          {payload.question_vi || payload.question_en}
        </div>
      ) : null}

      <Cue />

      {/* Options */}
      <div style={{
        display: 'grid', gap: 12,
        gridTemplateColumns: opts.length > 4 ? 'repeat(2, 1fr)' : 'repeat(2, 1fr)',
      }}>
        {opts.map((opt, i) => {
          const isCorrect = i === correctIdx;
          const isPicked = i === picked;
          let bg = HT.white;
          let border = HT.border;
          let badgeBg = HT.yellowBg;
          let badgeColor = HT.brownDark;
          let textColor = HT.text;
          if (isLocked) {
            if (isCorrect) {
              bg = HT.greenBg; border = HT.green;
              badgeBg = HT.green; badgeColor = HT.white; textColor = HT.greenDark;
            } else if (isPicked) {
              bg = HT.pinkBg; border = HT.coralDark;
              badgeBg = HT.coralDark; badgeColor = HT.white; textColor = HT.coralDark;
            } else {
              bg = HT.white; border = HT.border;
            }
          }
          return (
            <button
              key={i}
              onClick={() => click(i)}
              disabled={isLocked}
              style={{
                position: 'relative',
                padding: '18px 18px 18px 64px',
                background: bg,
                border: `2px solid ${border}`,
                borderRadius: 16,
                fontSize: 'clamp(16px, 1.8vw, 19px)',
                fontWeight: 700,
                fontFamily: FONT_BODY,
                color: textColor,
                cursor: isLocked ? 'default' : 'pointer',
                textAlign: 'left',
                boxShadow: isLocked ? 'none' : `0 3px 0 ${HT.border}`,
                transition: 'transform 0.1s, box-shadow 0.1s, background 0.2s, border-color 0.2s',
                minHeight: 64,
                display: 'flex', alignItems: 'center',
              }}
              onMouseDown={(e) => {
                if (isLocked) return;
                e.currentTarget.style.transform = 'translateY(2px)';
                e.currentTarget.style.boxShadow = `0 1px 0 ${HT.border}`;
              }}
              onMouseUp={(e) => {
                if (isLocked) return;
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = `0 3px 0 ${HT.border}`;
              }}
              onMouseLeave={(e) => {
                if (isLocked) return;
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = `0 3px 0 ${HT.border}`;
              }}
            >
              {/* Letter badge */}
              <span style={{
                position: 'absolute', left: 14, top: '50%',
                transform: 'translateY(-50%)',
                width: 36, height: 36, borderRadius: '50%',
                background: badgeBg, color: badgeColor,
                fontFamily: FONT_DISPLAY, fontSize: 16, fontWeight: 700,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                border: `2px solid ${HT.white}`,
                boxShadow: '0 1px 2px rgba(0,0,0,0.08)',
              }}>{OPT_LABELS[i]}</span>

              <span style={{ flex: 1 }}>{opt.text}</span>

              {isLocked && isCorrect && (
                <span style={{ fontSize: 22, marginLeft: 8 }}>✓</span>
              )}
              {isLocked && isPicked && !isCorrect && (
                <span style={{ fontSize: 22, marginLeft: 8 }}>✗</span>
              )}
            </button>
          );
        })}
      </div>

      {/* Explanation */}
      {isLocked && payload.explanation_vi ? (
        <div style={{
          marginTop: 16, padding: '14px 16px',
          background: HT.yellowBg, border: `1px solid ${HT.yellow}`,
          borderRadius: 14,
          fontSize: 14, color: HT.text, fontWeight: 600, lineHeight: 1.5,
        }}>
          <span style={{ fontFamily: FONT_DISPLAY, color: HT.brownDark, marginRight: 6 }}>💡 Mẹo:</span>
          {payload.explanation_vi}
        </div>
      ) : null}
    </div>
  );
}
